import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { Badge, Empty, Pagination, STATUS_TONE, fmtDate, label } from '../components/ui.jsx';
import { t } from '../i18n/index.jsx';
import InvoiceForm, { INVOICE_STATUSES, blankInvoice } from '../components/forms/InvoiceForm.jsx';
import { IconPlus } from '../components/icons.jsx';
import DataTable from '../components/DataTable.jsx';
import ExportButton from '../components/ExportButton.jsx';
import { cachedFetch, invalidate } from '../api/cache.js';

const statusTemplate = (row) => <Badge tone={STATUS_TONE[row.status]}>{label(row.status)}</Badge>;
const amountTemplate = (row) => `${Number(row.amount).toLocaleString()} ${row.currency}`;
const balanceTemplate = (row) => {
  const balance = Number(row.amount) - Number(row.paid_amount || 0);
  return <span className={balance > 0.01 ? 'due' : ''}>{balance.toLocaleString()} {row.currency}</span>;
};

export default function Invoices() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = user.role === 'admin' || user.role === 'manager';
  const [list, setList] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [ownCompanies, setOwnCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    params.set('page', String(page));
    params.set('per_page', '20');
    const path = `/invoices?${params}`;
    const res = await cachedFetch(path, () => api.get(path));
    setList(res.data);
    setMeta(res.meta || null);
  }, [status, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [status]);
  useEffect(() => {
    // Recipients = any company except our own — real commission invoices
    // often bill a manufacturer directly, not just "client"-typed companies.
    cachedFetch('/companies', () => api.get('/companies'))
      .then((r) => setRecipients(r.data.filter((c) => c.type !== 'own')))
      .catch(() => setRecipients([]));
    cachedFetch('/companies?type=own', () => api.get('/companies?type=own')).then((r) => setOwnCompanies(r.data)).catch(() => setOwnCompanies([]));
    cachedFetch('/projects', () => api.get('/projects')).then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  const columns = [
    { field: 'number', headerText: t('Number'), width: 160 },
    { field: 'client_name', headerText: t('Client'), width: 180 },
    { field: 'project_name', headerText: t('Project'), width: 160 },
    { headerText: t('Status'), width: 120, template: statusTemplate },
    { headerText: t('Amount'), width: 130, template: amountTemplate },
    { headerText: t('Balance due'), width: 130, template: balanceTemplate },
    { field: 'issue_date', headerText: t('Issued'), width: 110, template: (r) => fmtDate(r.issue_date) },
  ];

  return (
    <div className="page fade-in">
      <div className="page-head">
        <h1 className="page-title">{t('Invoices')}</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ExportButton
            filename="invoices"
            rows={list || []}
            columns={[
              { header: t('Number'), field: 'number' },
              { header: t('Client'), field: 'client_name' },
              { header: t('Project'), field: 'project_name' },
              { header: t('Status'), field: 'status' },
              { header: t('Amount'), value: (r) => r.amount },
              { header: t('Currency'), field: 'currency' },
              { header: t('Paid'), value: (r) => r.paid_amount ?? 0 },
              { header: t('Issued'), field: 'issue_date' },
            ]}
            disabled={!list?.length}
          />
          {canEdit && (
            <button type="button" className="btn primary" onClick={() => setEditing({ ...blankInvoice })}>
              <IconPlus width={15} height={15} /> {t('New invoice')}
            </button>
          )}
        </div>
      </div>

      <div className="toolbar">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t('All statuses')}</option>
          {INVOICE_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </select>
      </div>

      {!list ? (
        <DataTable data={[]} columns={columns} loading />
      ) : list.length === 0 ? (
        <Empty>{t('No invoices yet.')}</Empty>
      ) : (
        <DataTable
          data={list}
          columns={columns}
          onRowClick={(row) => navigate(`/invoices/${row.id}`)}
          onRowAction={(row) => navigate(`/invoices/${row.id}`)}
          actionLabel={t('Open')}
        />
      )}

      <Pagination meta={meta} onPage={setPage} />

      {editing && (
        <InvoiceForm
          initial={editing}
          recipients={recipients}
          ownCompanies={ownCompanies}
          projects={projects}
          onClose={() => setEditing(null)}
          onSaved={(res) => {
            invalidate('/invoices');
            setEditing(null);
            if (res?.id) navigate(`/invoices/${res.id}`);
            else load();
          }}
        />
      )}
    </div>
  );
}
